import React, { useEffect } from "react";
import { Box, Grid, TextField, Chip, Button, FormControl } from "@mui/material";
import DatePickerDialog from "../common/DatePickerDialog";
import { ListOptionDTO } from "axiosApi/models";
import { useApi } from "../../api/ApiProvider";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import Api from "../../axiosApi/api";
import intl from "components/i18n/ReactIntlWrapper";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { convertDateAsUTC2 } from "common/utils";
import DynamicAutocomplete from "../common/DynamicAutocomplete";
import { handleAPIError } from "../../common/errorHandler";

export type ReportsFilterUtilizationProps = {
  from: Date;
  to: Date;
  employees: string | null;
};

export const ReportsFilterUtilization = ({
  handleFilterChange,
  errors,
  setErrors,
}) => {

  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const refreshData = false;

  const currentDate = new Date();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );

  const [selectedEmployees, setSelectedEmployees] = React.useState<
    ListOptionDTO[]
  >([]);
  const [employees, setEmployees] = React.useState<ListOptionDTO[]>([]);
  const [from, setFrom] = React.useState<Date>(firstDayOfMonth);
  const [to, setTo] = React.useState<Date>(lastDayOfMonth);

  const filterValue = (list: ListOptionDTO[]): string =>
    list.map((e) => e.id).join(",");

  const handleEmployeeChange = (e, employees) => {
    setSelectedEmployees(employees);
    handleFilterChange({
      from: from,
      to: to,
      employees: filterValue(employees),
    });
  };

  const handleFrom = (e: string) => {
    setFrom(new Date(e));
    handleFilterChange({
      from: convertDateAsUTC2(new Date(e)),
      to: convertDateAsUTC2(to),
      employees: filterValue(selectedEmployees),
    });
  };

  const handleTo = (e: string) => {
    setTo(new Date(e));
    handleFilterChange({
      from: convertDateAsUTC2(from),
      to: convertDateAsUTC2(new Date(e)),
      employees: filterValue(selectedEmployees),
    });
  };

  const fetchEmployees = async () => {
    await api.employeeApi
      .apiVversionListEmployeesGet("1", false, {})
      .then((response) => {
        
        const employeesList : any = response.data?.data?.queryResult;
        if (Array.isArray(employeesList)) {
          setEmployees(
            employeesList.sort((a, b) => a.label.localeCompare(b.label))
          );
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  useEffect(() => {
    fetchEmployees();
    handleFilterChange({
      from: convertDateAsUTC2(from),
      to: convertDateAsUTC2(to),
      employees: filterValue(selectedEmployees),
    });
  }, [refreshData]);

  const handleReset = () => {
    setSelectedEmployees([]);
    setFrom(firstDayOfMonth);
    setTo(lastDayOfMonth);
    handleFilterChange({
      from: convertDateAsUTC2(firstDayOfMonth),
      to: convertDateAsUTC2(lastDayOfMonth),
      employees: "",
    });
  };

  return (
    <Box component="form" sx={{ my: 4 }}>
      <Grid container spacing={2}>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <DatePickerDialog
              disabled={false}
              inputName="date"
              className="timesheet-datepicker"
              selected={from}
              setSelected={handleFrom}
              required
            />
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <DatePickerDialog
              disabled={false}
              inputName="date"
              className="timesheet-datepicker"
              selected={to}
              setSelected={handleTo}
              required
            />
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
          <FormControl fullWidth>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleReset}
              startIcon={<CancelOutlinedIcon />}
            >
              {intl.get("ReportsFilterComponent.button.reset")}
            </Button>
          </FormControl>
        </Grid>
        <Grid
          size={{
            md: 3
          }}>
        </Grid>
        <Grid
          size={{
            md: 6
          }}>
          <FormControl fullWidth>
            <DynamicAutocomplete
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(employee, option) =>
                employee.id === option.id
              }
              onChange={handleEmployeeChange}
              options={employees}
              renderInput={(params) =>
                employees ? (
                  <TextField
                    {...params}
                    label={intl.get("reportsFilterComponent.team")}
                  />
                ) : (
                  ""
                )
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id.toString()}>
                  {option.label}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    color="primary"
                    variant="outlined"
                    {...getTagProps({ index })}
                  />
                ))
              }
              value={selectedEmployees}
            />
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsFilterUtilization;
